"use client";

/** THE WORD AFTER A COMPANY SIGN-IN (plan 81; VC-Authz, 30 Oct). The server
 *  puts a one-off `notice` on /auth/user after a sign-in that made the
 *  company administrator into an organisation with no owner, to choose one
 *  ("choose_owner", Tom 2026-10-31), or that made the
 *  person owner of their company's organisation ("now_owner", any
 *  sign-in), or after the verify page's sign-in by someone holding no
 *  administrator role ("no_admin_role"), or with an account in no company's
 *  directory ("no_company", about no organisation), or after a Google sign-in
 *  that landed in the personal space while the company's organisation takes
 *  no newcomers ("joining_paused"). Reading /auth/user never clears
 *  it; the page clears it once it has it (POST /auth/notice/consume), and
 *  keeps it for the tab's session until dismissed, as the removal notice
 *  does. */

import React, { useEffect, useState } from "react";
import {
  consumeSignInNotice,
  noticeOrganisation,
  providerWords,
  signInNoticeOf,
  type SignInNotice,
} from "@sourcepilot/ui/framework/data/company-org";

const KEY = "sp-sign-in-notice";

function readKept(): SignInNotice | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? signInNoticeOf({ notice: JSON.parse(raw) }) : null;
  } catch {
    return null;
  }
}

function keep(n: SignInNotice | null): void {
  try {
    if (n) sessionStorage.setItem(KEY, JSON.stringify(n));
    else sessionStorage.removeItem(KEY);
  } catch { /* no storage: shown for this render only */ }
}

export function useSignInNotice(user: unknown): { notice: SignInNotice | null; dismiss: () => void } {
  const [notice, setNotice] = useState<SignInNotice | null>(() => readKept());
  const fresh = signInNoticeOf(user);
  const key = fresh ? `${fresh.kind}:${noticeOrganisation(fresh)?.id ?? ""}` : null;
  useEffect(() => {
    if (!fresh) return;
    keep(fresh);
    setNotice(fresh);
    void consumeSignInNotice();
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return { notice, dismiss: () => { keep(null); setNotice(null); } };
}

const MOVED = "Your domain belonged to another Google Workspace before; that Workspace keeps its own organisation, and this one is new.";

/** The notice's sentence. */
export function signInNoticeWords(n: SignInNotice): string {
  const google = n.provider === "google";
  if (n.kind === "no_company") {
    return google
      ? "That account isn't in a company's Google Workspace. Sign in with your work account."
      : `That account isn't a work account in a company's ${providerWords("microsoft")} directory. Sign in with your work account.`;
  }
  if (n.kind === "joining_paused") {
    return "Your company's organisation isn't taking new members right now: a Google Workspace administrator needs to verify it again. You're in your personal space until then.";
  }
  if (n.kind === "choose_owner" || n.kind === "now_owner") {
    const said =
      n.kind === "choose_owner"
        ? // a company administrator chooses; Google: people only, until
          // Google Groups are read (stage 8)
          `${n.organisation.name ?? "This organisation"} has no owner yet. Make yourself the owner, or choose ${google ? "a person" : "a person or a group"}.`
        : `You're now the owner of ${n.organisation.name ?? "your organisation"}. The organisation is verified.`;
    return n.domain_moved ? `${said} ${MOVED}` : said;
  }
  // about no organisation: a Google one whose joining is paused
  if (!n.organisation) {
    return "Your Google account isn't a Google Workspace administrator who can see your organisation's domains, so joining stays paused.";
  }
  const org = n.organisation.name ?? "your organisation";
  // Google: the proof is reading the company's domains, not a role name
  return google
    ? `Your Google account isn't a Google Workspace administrator who can see your organisation's domains, so ${org} stays unverified.`
    : `Your ${providerWords("microsoft")} account doesn't hold an administrator role, so ${org} stays unverified.`;
}

export function SignInNoticeBanner({ notice, onDismiss }: { notice: SignInNotice | null; onDismiss: () => void }) {
  if (!notice) return null;
  const owner = notice.kind === "now_owner";
  const org = noticeOrganisation(notice);
  return (
    <div role="status" data-testid="sign-in-notice"
      className={`flex items-center justify-center gap-3 border-b px-4 py-2 text-sm ${owner ? "border-emerald-300 bg-emerald-50 text-emerald-900" : "border-amber-300 bg-amber-50 text-amber-900"}`}>
      <span>{signInNoticeWords(notice)}</span>
      {org && (
        <a className="underline" href={`/organisations/${encodeURIComponent(org.id)}`} data-testid="sign-in-notice-open">
          {notice.kind === "choose_owner" ? "Choose the owner" : `Open ${org.name ?? "the organisation"}`}
        </a>
      )}
      <button className="rounded border bg-white px-2 py-0.5 text-xs hover:bg-gray-50"
        onClick={onDismiss} data-testid="sign-in-notice-dismiss">
        Dismiss
      </button>
    </div>
  );
}
